import React from 'react';
import { Checkbox, Select } from 'antd';
import './ScopeFilterRow.css';

export type ScopeFilterVariant = 'country-centre' | 'centre-school';

type FilterOption = { value: string | number; label: string };

const VARIANT_CONFIG: Record<
  ScopeFilterVariant,
  {
    checkboxLabel: string;
    primaryLabel: string;
    secondaryLabel: string;
    primaryPlaceholder: string;
    secondaryPlaceholder: string;
  }
> = {
  'country-centre': {
    checkboxLabel: 'Apply to specific centre',
    primaryLabel: 'Country',
    secondaryLabel: 'Centre',
    primaryPlaceholder: 'Select Country',
    secondaryPlaceholder: 'Select Centre',
  },
  'centre-school': {
    checkboxLabel: 'Apply to specific school',
    primaryLabel: 'Centre',
    secondaryLabel: 'School',
    primaryPlaceholder: 'Select Centre',
    secondaryPlaceholder: 'Select School',
  },
};

export interface ScopeFilterRowProps {
  variant: ScopeFilterVariant;
  useSpecificScope: boolean;
  onUseSpecificScopeChange: (checked: boolean) => void;
  primaryOptions: FilterOption[];
  secondaryOptions: FilterOption[];
  primaryValue?: string | number;
  secondaryValue?: string | number;
  onPrimaryChange: (value: string | number | undefined) => void;
  onSecondaryChange: (value: string | number | undefined) => void;
  trailing?: React.ReactNode;
  className?: string;
}

const ScopeFilterRow: React.FC<ScopeFilterRowProps> = ({
  variant,
  useSpecificScope,
  onUseSpecificScopeChange,
  primaryOptions,
  secondaryOptions,
  primaryValue,
  secondaryValue,
  onPrimaryChange,
  onSecondaryChange,
  trailing,
  className,
}) => {
  const config = VARIANT_CONFIG[variant];
  const scopeLabel = useSpecificScope ? config.secondaryLabel : config.primaryLabel;
  const scopeOptions = useSpecificScope ? secondaryOptions : primaryOptions;
  const scopeValue = useSpecificScope ? secondaryValue : primaryValue;
  const scopePlaceholder = useSpecificScope
    ? config.secondaryPlaceholder
    : config.primaryPlaceholder;
  const onScopeChange = useSpecificScope ? onSecondaryChange : onPrimaryChange;

  return (
    <div className={['scope-filter-row', className].filter(Boolean).join(' ')}>
      <Checkbox
        checked={useSpecificScope}
        onChange={(e) => {
          const checked = e.target.checked;
          onUseSpecificScopeChange(checked);
          if (checked) {
            onPrimaryChange(undefined);
          } else {
            onSecondaryChange(undefined);
          }
        }}
      >
        {config.checkboxLabel}
      </Checkbox>
      <span className="scope-filter-label">{scopeLabel}</span>
      <Select<string | number>
        allowClear
        placeholder={scopePlaceholder}
        style={{ width: 260 }}
        options={scopeOptions}
        value={scopeValue}
        showSearch={{ optionFilterProp: 'label' }}
        onChange={(rawId) => {
          if (rawId == null || (typeof rawId === 'string' && rawId.trim() === '')) {
            onScopeChange(undefined);
            return;
          }
          onScopeChange(rawId);
        }}
      />
      {trailing}
    </div>
  );
};

export default ScopeFilterRow;